import { Toaster } from "@/components/ui/sonner";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Paywall from "@/pages/Paywall";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Inboxes from "@/pages/Inboxes";
import InboxDetail from "@/pages/InboxDetail";
import Campaigns from "@/pages/Campaigns";
import CampaignWizard from "@/pages/CampaignWizard";
import CampaignDetail from "@/pages/CampaignDetail";
import Analytics from "@/pages/Analytics";
import Analyzer from "@/pages/Analyzer";
import Settings from "@/pages/Settings";
import Audit from "@/pages/Audit";
import AuditFunnel from "@/pages/AuditFunnel";
import AuditResult from "@/pages/AuditResult";
import AuditSuccess from "@/pages/AuditSuccess";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Leads from "@/pages/Leads";
import Legal from "@/pages/Legal";
import Unibox from "@/pages/Unibox";
import WorkspaceSettings from "@/pages/WorkspaceSettings";
import "@/App.css";

/** Wrapper that requires auth. Unsubscribed users get the Paywall. */
function ProtectedRoute({ children, allowUnsubscribed = false }) {
  const { user, billing, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="text-neutral-500 text-sm font-mono">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!allowUnsubscribed && billing && !billing.has_access) {
    return <Paywall />;
  }
  return children;
}

/** Public root - landing page. Logged-in subscribed users go to /dashboard. */
function RootRouter() {
  const { user, billing, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="text-neutral-500 text-sm font-mono">Loading…</div>
      </div>
    );
  }
  if (user && billing?.has_access) {
    return <Navigate to="/dashboard" replace />;
  }
  if (user && billing && !billing.has_access) {
    return <Navigate to="/paywall" replace />;
  }
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootRouter />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Public marketing funnel — paid Stripe AI Audit. */}
      <Route path="/free-audit" element={<AuditFunnel />} />
      <Route path="/audit/success" element={<AuditSuccess />} />
      {/* Public auditor — sidebar layout, NO ProtectedRoute. Anonymous OK. */}
      <Route element={<AppLayout />}>
        <Route path="/audit" element={<Audit />} />
        <Route path="/audit/result/:id" element={<AuditResult />} />
      </Route>
      {/* Magic-link landing - public on purpose; the token IS the auth. */}
      <Route path="/verify-email" element={<VerifyEmail />} />
      {/* Public legal pages */}
      <Route path="/terms" element={<Legal doc="terms" />} />
      <Route path="/privacy" element={<Legal doc="privacy" />} />
      {/* Billing - accessible to authenticated users regardless of subscription */}
      <Route
        path="/paywall"
        element={
          <ProtectedRoute allowUnsubscribed>
            <Paywall />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute allowUnsubscribed>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Billing />} />
      </Route>
      <Route
        path="/billing/success"
        element={
          <ProtectedRoute allowUnsubscribed>
            <BillingSuccess />
          </ProtectedRoute>
        }
      />
      {/* Core app - gated by ProtectedRoute */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/inboxes" element={<Inboxes />} />
        <Route path="/inboxes/:id" element={<InboxDetail />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/campaigns/new" element={<CampaignWizard />} />
        <Route path="/campaigns/:id" element={<CampaignDetail />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/analyzer" element={<Analyzer />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/unibox" element={<Unibox />} />
        <Route path="/workspace" element={<WorkspaceSettings />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
